import { defineMessages, type MessageDescriptor } from "react-intl";

import { pickAssessmentCopyVariant, type AssessmentCopyVariants } from "@/modules/candidate/assessmentCopy";

import { IAssessmentTest, ILocalizedTestElement } from "@/modules/assessments/types/test.interface";

import { minToSec } from "@/shared/utils/common.utils";
import { sortNumbers } from "@/shared/utils/array/sort.utils";

import { TestAnswerStatusEnum } from "@/shared/services/tests/tests.types";
import { IAnswer, ITestAnswer } from "@/shared/components/charts/types/chartTypes";
import { TTestAnswer } from "@/shared/hooks/initializer/use-initialize-candidate.hook";
import { ITestChoice, QuestionTypes } from "@/shared/components/candidate/scenes/TestElement/TestElement";

import { AssessmentInviteStatus } from "@/modules/assessments/scenes/AssessmentInvites/invites.constants";

import { getTestElementSection, getTestTimes } from "./section.utils";
import { CandidateAssessmentViewTypes } from "../CandidateAssessment";

export const mainButtonMessages = defineMessages({
  registerToStart: { id: "candidate.welcome.registerToStart", defaultMessage: "register to start" },
  continueButton: { id: "candidate.welcome.continueButton", defaultMessage: "continue" },
  startAssessment: { id: "candidate.welcome.assessment.start", defaultMessage: "start assessment" },
  startSurvey: { id: "candidate.welcome.survey.start", defaultMessage: "start survey" },
});

export const startMessages: AssessmentCopyVariants = {
  assessment: mainButtonMessages.startAssessment,
  survey: mainButtonMessages.startSurvey,
};

/**
 * @param {boolean} isAuthenticated - whether the candidate is signed in
 * @param {AssessmentInviteStatus} [inviteStatus] - the invite's current status
 * @param {boolean} isSurvey - whether the assessment is flagged as an org survey
 * @returns {MessageDescriptor} the message for the main call-to-action button
 */
export function getMainButtonTitleMessage({
  isAuthenticated,
  inviteStatus,
  isSurvey,
}: {
  isAuthenticated: boolean;
  inviteStatus?: AssessmentInviteStatus;
  isSurvey: boolean;
}): MessageDescriptor {
  if (!isAuthenticated) {
    return mainButtonMessages.registerToStart;
  }

  if (inviteStatus === AssessmentInviteStatus.InProgress || inviteStatus === AssessmentInviteStatus.Started) {
    return mainButtonMessages.continueButton;
  }

  return pickAssessmentCopyVariant({ variants: startMessages, isSurvey });
}

export function getViewType(
  questionsList: ILocalizedTestElement[][],
  currentPage: number,
): CandidateAssessmentViewTypes {
  if (questionsList && questionsList[currentPage - 1]) {
    return CandidateAssessmentViewTypes.test;
  }

  return currentPage === 0 ? CandidateAssessmentViewTypes.start : CandidateAssessmentViewTypes.finish;
}

export function getQuestionElements(questionsList: ILocalizedTestElement[]) {
  if (!questionsList || !questionsList.length) {
    return [];
  }

  return questionsList?.filter(({ config }) => config.type !== QuestionTypes.Text);
}

export function hasDuplicateMostLeastAnswers(
  currentAnswers: ITestChoice[],
  currentPageQuestions: ILocalizedTestElement[],
): boolean {
  for (let i = 0; i < currentPageQuestions.length - 1; i++) {
    const currentQuestion = currentPageQuestions[i];
    if (currentQuestion.config.type !== QuestionTypes.MostLeast) continue;

    const nextQuestion = currentPageQuestions[i + 1];
    if (nextQuestion.config.type !== QuestionTypes.MostLeast) continue;

    const firstAnswer = currentAnswers.find((a) => a.elementId === currentQuestion.elementId)?.choiceKey;
    const secondAnswer = currentAnswers.find((a) => a.elementId === nextQuestion.elementId)?.choiceKey;

    if (firstAnswer && secondAnswer && firstAnswer.split("-").at(-1) === secondAnswer.split("-").at(-1)) {
      return true;
    }

    i++; // pairs are always adjacent
  }
  return false;
}

export function areAllRequiredQuestionsAnswered(
  currentAnswers: ITestChoice[],
  questionsList: ILocalizedTestElement[][],
  currentPage: number,
) {
  if (!questionsList[currentPage - 1]?.length) {
    return false;
  }

  return !questionsList[currentPage - 1].some((question) => {
    if (question.config.required) {
      return !currentAnswers.find((answer) => answer.elementId === question.elementId)?.choiceKey;
    }

    return false;
  });
}

export function areAllQuestionsAnswered(
  currentAnswers: ITestChoice[],
  questionsList: ILocalizedTestElement[][],
  currentPage: number,
) {
  return (
    currentAnswers.filter((answer) => answer.choiceKey).length ===
    questionsList[currentPage - 1].filter((question) => question.config.type !== QuestionTypes.Text).length
  );
}

export function getIfPrevQuestionNewTest(questionsList: ILocalizedTestElement[][], currentPage: number) {
  if (!currentPage) {
    return false;
  }

  const prevQuestion = questionsList[currentPage - 2];
  const currentQuestion = questionsList[currentPage - 1];

  if (!prevQuestion) {
    return true;
  }

  if (!currentQuestion) {
    return false;
  }

  return currentQuestion[0].testId !== prevQuestion[0].testId;
}

export function getIfNextQuestionNewTest(questionsList: ILocalizedTestElement[][], currentPage: number) {
  if (!currentPage) {
    return false;
  }

  const currentQuestion = questionsList[currentPage - 1];
  const nextQuestion = questionsList[currentPage];

  if (!currentQuestion) {
    return false;
  }

  if (!nextQuestion) {
    return true;
  }

  return currentQuestion[0].testId !== nextQuestion[0].testId;
}

export function getMissedAnswerQuestionsKeys(
  questionsList: ILocalizedTestElement[][],
  answers: ITestAnswer[],
  testId?: number,
  isRedirect = true,
) {
  const testAnswer: IAnswer[] = answers.reduce((testAnswers: IAnswer[], { answer }) => [...testAnswers, ...answer], []);

  if (!testAnswer) {
    return [];
  }

  const missedQuestionIndexes: number[] = [];

  questionsList.forEach((elements, questionIndex) => {
    if (testId && elements[0].testId !== testId) {
      return;
    }

    const questions = elements.filter(({ config }) => config.type !== QuestionTypes.Text);
    const answeredQuestions = questions.filter(({ config }) =>
      testAnswer.find(({ elementKey, answerKey }) => elementKey === config.key && answerKey),
    );

    const ifNotExistQuestionAnswer = answeredQuestions.length !== questions.length || !answeredQuestions.length;

    if (ifNotExistQuestionAnswer) {
      missedQuestionIndexes.push(questionIndex);
    }
  });

  const sectionsTime = getTestTimes(answers, testId);

  if (!missedQuestionIndexes.length && isRedirect) {
    const testQuestionIndex = questionsList.findIndex((elements) => elements[0].testId === testId);
    return [testQuestionIndex];
  }

  const filteredMissedQuestionIndexes = missedQuestionIndexes.filter((indexPage) => {
    // if page has only Text elements -> ignore it
    if (!questionsList[indexPage].some((element) => element.config.type !== QuestionTypes.Text)) {
      return false;
    }

    if (!testId || !sectionsTime) {
      return true;
    }

    const section = getTestElementSection(questionsList, testId, indexPage + 1);

    if (!section || !section.config.startsSection?.timeLimit) {
      return true;
    }

    const sectionTime = sectionsTime[section.config.key];

    if (!sectionTime) {
      return true;
    }

    return sectionTime.time < minToSec(section.config.startsSection?.timeLimit);
  });

  return filteredMissedQuestionIndexes;
}

export function getNextQuestionPage(
  currentPage: number,
  questionsList: ILocalizedTestElement[][],
  answers: ITestAnswer[],
  mustBeSeparateTest: boolean = false,
): number {
  const currentTestId = questionsList[currentPage - 1][0].testId;

  const nextInProgressQuestionPageIndex = questionsList.findIndex((elements, questionIndex) => {
    // If questionIndex is less than currentPage, skip it because we're not going back
    // Also, there may not be a "done" matchingTestAnswer for the current page, yet.
    if (questionIndex < currentPage) {
      return false;
    }

    // In the "run out of time" case, we have to move forward to the next test,
    // avoiding the next page(s) of the current test.
    if (mustBeSeparateTest && currentTestId === elements[0].testId) {
      return false;
    }

    // Exclude pages from tests that are already "done" - they should not be navigated to.
    // Check the test status for the page's testId, not just the matchingTestAnswer
    const pageTestId = elements[0].testId;
    const testAnswerDetailsForPage = answers.find(({ testId }) => testId === pageTestId);
    if (testAnswerDetailsForPage && testAnswerDetailsForPage.status === TestAnswerStatusEnum.Done) {
      return false;
    }

    const matchingTestAnswer = answers.find(({ answer }) => {
      // If we find an answer record related to the in-question page of questions from the
      // findIndex above, return it
      return answer.find(({ elementId }) => elementId === elements[0].elementId);
    });

    // If there ISN'T a matchingTestAnswer for the page in-question, that set of questions is next.
    // If there IS a matchingTestAnswer, check if it's in progress. If so, it's next.
    return !matchingTestAnswer || (matchingTestAnswer && matchingTestAnswer.status === TestAnswerStatusEnum.InProgress);
  });

  // If we didn't find any future not-started or in-progress question page,
  // return the last page + 1 to go to the finish page.
  const returnValue =
    nextInProgressQuestionPageIndex === -1 ? questionsList.length + 1 : nextInProgressQuestionPageIndex + 1;
  return returnValue;
}

export function getIfTheEndOfTheAssessment(
  questionsList: ILocalizedTestElement[][],
  currentPage: number,
  testId?: number,
) {
  if (!questionsList[currentPage]) {
    return true;
  }

  return !Boolean(
    questionsList
      .slice(currentPage)
      ?.find(
        (elements) =>
          (elements && elements.find((element) => element.config.startsSection) && elements[0].testId === testId) ||
          (elements[0] && elements[0].testId !== testId),
      ),
  );
}

export function getWouldTimeoutLeadToFinish({
  currentPage,
  questionsList,
  answers,
}: {
  currentPage: number;
  questionsList: ILocalizedTestElement[][];
  answers: ITestAnswer[];
}): boolean {
  const nextPage = getNextQuestionPage(currentPage, questionsList, answers, true);
  return nextPage > questionsList.length;
}

export function findQuestionAnswers(answers: ITestAnswer[], questions: ILocalizedTestElement[]) {
  const questionAnswers: ITestChoice[] = [];

  const filteredAnswers = answers.filter(({ testId }) => questions && questions[0]?.testId === testId);

  if (!filteredAnswers) {
    return questionAnswers;
  }

  const questionAnswer = filteredAnswers[0];

  questionAnswer?.answer.forEach(({ elementKey, elementId, answerKey }) => {
    const isQuestionAnswer = questions.find((question) => question.config.key === elementKey);

    if (isQuestionAnswer) {
      questionAnswers.push({ elementId, choiceKey: answerKey });
    }
  });

  return questionAnswers;
}

export function findNextTestQuestions(questionsList: ILocalizedTestElement[][], testAnswers: TTestAnswer[]) {
  return questionsList.find((questions) => testAnswers.find((testAnswer) => testAnswer.testId !== questions[0].testId));
}

export function findCurrentTestId(
  questionsList: ILocalizedTestElement[][],
  testAnswers: TTestAnswer[],
  questionsIndex: number,
  assessmentTests?: IAssessmentTest[],
) {
  let testId = 0;

  if (questionsList[questionsIndex]) {
    testId = questionsList[questionsIndex][0].testId;
  }

  if (testAnswers) {
    const testAnswer = testAnswers.find((testAnswer) => testAnswer.testId === testId);

    if (testAnswer && testAnswer.status === TestAnswerStatusEnum.Done && assessmentTests?.length) {
      if (testAnswers.find((testAnswer) => testAnswer.testId === questionsList[questionsIndex][0].testId)) {
        const nextTestQuestions = findNextTestQuestions(questionsList.slice(questionsIndex), testAnswers);

        if (nextTestQuestions && nextTestQuestions.length) {
          testId = nextTestQuestions[0].testId;
        }
      }

      const sortedAssessmentTests = assessmentTests.sort((a, b) => sortNumbers(a.rank, b.rank));

      const allowedAssessmentTest = sortedAssessmentTests.find((assessmentTest) =>
        testAnswers.find(
          (tAnswer) => tAnswer.testId === assessmentTest.testId && tAnswer.status !== TestAnswerStatusEnum.Done,
        ),
      );

      if (allowedAssessmentTest) {
        testId = allowedAssessmentTest.testId;
      } else {
        const notFinishedTests = sortedAssessmentTests.filter(
          (assessmentTest) =>
            !testAnswers.find(
              (tAnswer) => tAnswer.testId === assessmentTest.testId && tAnswer.status === TestAnswerStatusEnum.Done,
            ),
        );

        if (notFinishedTests.length && notFinishedTests[0]) {
          testId = notFinishedTests[0].testId;
        }
      }
    }
  }

  return testId;
}

// The landing page renders above the intro in ContentView, so none of the intro's prerequisites — the
// language picker, terms/attestation, demographics — mount there, and its button starts the assessment
// directly. `?page=landing` is an ordinary URL that the intro's own navigate() puts into history, so a
// reload, a Back/Forward, or a shared link reaches it without passing through any of them.
//
// `intakeCompleted` covers the one hop where server truth lags: the intake submit navigates straight to
// landing without refreshing the invite, so demographicsSubmittedAt and employeeId are still empty in
// memory. Every later load reads them from the server.
export function areIntroPrerequisitesMet({
  intakeCompleted,
  hasAcceptedTerms,
  isMultiLocaleAssessment,
  hasConfirmedLocale,
  demographicsEnabled,
  demographicsSubmittedAt,
  employeeIdEnabled,
  employeeId,
}: {
  intakeCompleted: boolean;
  hasAcceptedTerms: boolean;
  isMultiLocaleAssessment: boolean;
  hasConfirmedLocale: boolean;
  demographicsEnabled?: boolean;
  demographicsSubmittedAt?: string | null;
  employeeIdEnabled?: boolean;
  employeeId?: string | null;
}): boolean {
  if (intakeCompleted) return true;

  const localeChosen = !isMultiLocaleAssessment || hasConfirmedLocale;
  const demographicsCollected = !demographicsEnabled || Boolean(demographicsSubmittedAt);
  const employeeIdCollected = !employeeIdEnabled || Boolean(employeeId?.trim());

  return hasAcceptedTerms && localeChosen && demographicsCollected && employeeIdCollected;
}
